'use client';

// The fields of the "Company profile" filter group, one component each.
// State stays in the sidebar; ProfileFilterGroup hands its props through.
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Info } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover } from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterCheckbox,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { RECHTSFORM_OPTIONS } from '../searchConstants';
import {
  COMPANY_SIZE_HELP_EN,
  COMPANY_SIZE_OPTIONS,
  CURRENT_YEAR,
  LEGAL_STATUS_HELP_EN,
  LEGAL_STATUS_OPTIONS,
  MEMBERSHIP_OPTIONS,
  TENDER_ROLE_HELP_EN,
  TENDER_ROLE_OPTIONS,
} from '../searchSidebarOptions';
import {
  ComboboxContent,
  ComboboxTrigger,
  FilterChip,
  OptionCheck,
  submitOnEnter,
} from './FilterFieldParts';
import type { ProfileFilterGroupProps } from './ProfileFilterGroup';

type Lock = 'user' | 'pro' | null;

export function RechtsformField({
  t,
  rechtsformOpen,
  setRechtsformOpen,
  staged,
  removeSet,
  set,
  rechtsformOptions,
  toggleSet,
  facetSum,
}: Pick<
  ProfileFilterGroupProps,
  | 't'
  | 'rechtsformOpen'
  | 'setRechtsformOpen'
  | 'staged'
  | 'removeSet'
  | 'set'
  | 'rechtsformOptions'
  | 'toggleSet'
  | 'facetSum'
>) {
  const selectedRechtsformen = staged.rechtsformCodes;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.rechtsform', { defaultValue: 'Legal form' })}
      </Label>
      <Popover open={rechtsformOpen} onOpenChange={setRechtsformOpen}>
        <ComboboxTrigger>
          {selectedRechtsformen.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.rechtsformPlaceholder', {
                defaultValue: 'e.g. GmbH, AG…',
              })}
            </span>
          ) : (
            selectedRechtsformen.map((code) => {
              const opt = RECHTSFORM_OPTIONS.find(
                (o) => o.codes.join(',') === code,
              );
              return (
                <FilterChip
                  key={code}
                  onRemove={() => removeSet(code, set.rechtsformCodes)}
                >
                  <span className="max-w-[220px] truncate">
                    {opt?.label ?? code}
                  </span>
                </FilterChip>
              );
            })
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command>
            <CommandInput
              className={INPUT_PLACEHOLDER_CLASS}
              placeholder={t('search.sidebar.searchRechtsform', {
                defaultValue: 'Search legal forms…',
              })}
            />
            <CommandList>
              <CommandEmpty>
                {t('search.sidebar.noRechtsformFound', {
                  defaultValue: 'No legal form found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {rechtsformOptions.map((opt) => {
                  const key = opt.codes.join(',');
                  return (
                    <CommandItem
                      key={key}
                      value={opt.label}
                      keywords={opt.keywords}
                      onSelect={() => toggleSet(key, set.rechtsformCodes)}
                    >
                      <OptionCheck
                        selected={selectedRechtsformen.includes(key)}
                      />
                      <span className="truncate text-xs">{opt.label}</span>
                      <FacetCount
                        count={facetSum('rechtsform_code', opt.codes)}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** Founded between — two 4-digit year inputs. `inputMode="numeric"`
 *  triggers the numeric keypad on mobile. The backend gets
 *  ${from}-01-01 / ${to}-12-31 in SearchPageClient. */
export function FoundedBetweenField({
  t,
  staged,
  set,
  handleFormSubmit,
}: Pick<ProfileFilterGroupProps, 't' | 'staged' | 'set' | 'handleFormSubmit'>) {
  const { foundedAtFrom, foundedAtTo } = staged;
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.foundedBetween', {
          defaultValue: 'Founded between',
        })}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          min={1800}
          max={CURRENT_YEAR}
          value={foundedAtFrom}
          onChange={(e) =>
            set.foundedAtFrom(e.target.value.replace(/\D/g, '').slice(0, 4))
          }
          onKeyDown={submitOnEnter(handleFormSubmit)}
          aria-label={t('search.sidebar.foundedFrom', {
            defaultValue: 'Founded from',
          })}
          placeholder="1990"
          className={`flex-1 h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
        />
        <span className="text-muted-foreground text-xs shrink-0">—</span>
        <Input
          type="number"
          inputMode="numeric"
          min={1800}
          max={CURRENT_YEAR}
          value={foundedAtTo}
          onChange={(e) =>
            set.foundedAtTo(e.target.value.replace(/\D/g, '').slice(0, 4))
          }
          onKeyDown={submitOnEnter(handleFormSubmit)}
          aria-label={t('search.sidebar.foundedTo', {
            defaultValue: 'Founded to',
          })}
          placeholder={String(CURRENT_YEAR)}
          className={`flex-1 h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
        />
      </div>
    </div>
  );
}

/** Memberships — multi-select. Backend OR-merges. Pro-gated: this is the
 *  one filter that comes from data we assembled ourselves (the
 *  professional-registry matcher), not from a public register, so it is the
 *  most valuable to bulk-extract and the least reproducible elsewhere. */
export function MembershipsField({
  t,
  lockOf,
  membershipsOpen,
  setMembershipsOpen,
  staged,
  removeSet,
  set,
  membershipOptions,
  toggleSet,
  facetCount,
}: Pick<
  ProfileFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'membershipsOpen'
  | 'setMembershipsOpen'
  | 'staged'
  | 'removeSet'
  | 'set'
  | 'membershipOptions'
  | 'toggleSet'
  | 'facetCount'
>) {
  const selectedMemberships = staged.memberships;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('memberships')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.memberships', { defaultValue: 'Memberships' })}
      </FilterLabel>
      <Popover
        open={lockOf('memberships') ? false : membershipsOpen}
        onOpenChange={setMembershipsOpen}
      >
        <ComboboxTrigger disabled={!!lockOf('memberships')}>
          {selectedMemberships.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.membershipsPlaceholder', {
                defaultValue: 'e.g. BaFin, Steuerberaterkammer…',
              })}
            </span>
          ) : (
            selectedMemberships.map((value) => {
              const opt = MEMBERSHIP_OPTIONS.find((o) => o.value === value);
              return (
                <FilterChip
                  key={value}
                  onRemove={() => removeSet(value, set.memberships)}
                >
                  <span className="max-w-[220px] truncate">
                    {opt?.label ?? value}
                  </span>
                </FilterChip>
              );
            })
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command>
            <CommandList>
              <CommandGroup>
                {membershipOptions.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() =>
                      toggleSet(opt.value, set.memberships)
                    }
                  >
                    <OptionCheck
                      selected={selectedMemberships.includes(opt.value)}
                    />
                    <span className="truncate text-xs">{opt.label}</span>
                    <FacetCount count={facetCount('membership', opt.value)} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

type CheckboxOption = {
  readonly value: string;
  readonly labelKey: string;
  readonly defaultLabel: string;
};

/**
 * A headed list of checkboxes, one per option, with an ⓘ that explains every
 * option. Legal status, company size and public procurement all use it; a
 * new multi-value checkbox filter with per-option help is one more caller.
 */
function OptionCheckboxField({
  t,
  lockOf,
  control,
  label,
  infoLabel,
  options,
  helpEn,
  extraHelp,
  selected,
  setSelected,
  facetCount,
  facet,
}: {
  t: TFunction<'translation', undefined>;
  lockOf: (control: string) => Lock;
  /** The gating key passed to `lockOf`. */
  control: string;
  label: string;
  infoLabel: string;
  options: readonly CheckboxOption[];
  /** English fallback of each option's `${labelKey}Help` text, by value. */
  helpEn: Record<string, string>;
  /** Rendered after the per-option help, inside the ⓘ. */
  extraHelp?: ReactNode;
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  facetCount: (facet: string, value: string) => number | undefined;
  facet: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <FilterLabel lock={lockOf(control)} className={FIELD_LABEL_CLASS}>
          {label}
        </FilterLabel>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={infoLabel}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              align="start"
              className="max-w-[19rem] text-xs"
            >
              <dl className="space-y-1.5">
                {options.map((opt) => (
                  <div key={opt.value}>
                    <dt className="font-medium">
                      {t(`search.sidebar.${opt.labelKey}`, {
                        defaultValue: opt.defaultLabel,
                      })}
                    </dt>
                    <dd className="text-muted-foreground">
                      {t(`search.sidebar.${opt.labelKey}Help`, {
                        defaultValue: helpEn[opt.value],
                      })}
                    </dd>
                  </div>
                ))}
                {extraHelp}
              </dl>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="space-y-0.5">
        {options.map((opt) => (
          <FilterCheckbox
            key={opt.value}
            checked={selected.includes(opt.value)}
            onCheckedChange={(v) =>
              setSelected((prev) =>
                v ? [...prev, opt.value] : prev.filter((x) => x !== opt.value),
              )
            }
            lock={lockOf(control)}
            label={t(`search.sidebar.${opt.labelKey}`, {
              defaultValue: opt.defaultLabel,
            })}
            count={facetCount(facet, opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

type OptionFieldProps = Pick<
  ProfileFilterGroupProps,
  't' | 'lockOf' | 'facetCount' | 'staged' | 'set'
>;

/** Legal status — five disjoint buckets, resolved server-side from the
 *  merged Handelsregister + Insolvenzbekanntmachungen timeline. The ⓘ is not
 *  decoration: "in liquidation", "dissolved" and "insolvent" are three
 *  distinct legal states that all look like "not trading" from outside. */
export function LegalStatusField({
  t,
  lockOf,
  facetCount,
  staged,
  set,
}: OptionFieldProps) {
  return (
    <OptionCheckboxField
      t={t}
      lockOf={lockOf}
      control="legalStatus"
      label={t('search.sidebar.legalStatus', {
        defaultValue: 'Legal status',
      })}
      infoLabel={t('search.sidebar.legalStatusInfoLabel', {
        defaultValue: 'What do these statuses mean?',
      })}
      options={LEGAL_STATUS_OPTIONS}
      helpEn={LEGAL_STATUS_HELP_EN}
      selected={staged.legalStatus}
      setSelected={set.legalStatus}
      facetCount={facetCount}
      facet="legal_status"
    />
  );
}

/** Company size — the § 267 HGB classes. Derived from what the company was
 *  legally required to publish, not from a figure it filed: withholding the
 *  profit-and-loss statement is a relief only small companies may claim. So
 *  this reaches ~761k companies that have no turnover on file at all, which
 *  is the whole point of it. Ticking every box still filters. */
export function CompanySizeField({
  t,
  lockOf,
  facetCount,
  staged,
  set,
}: OptionFieldProps) {
  return (
    <OptionCheckboxField
      t={t}
      lockOf={lockOf}
      control="companySize"
      label={t('search.sidebar.companySize', {
        defaultValue: 'Company size',
      })}
      infoLabel={t('search.sidebar.companySizeInfoLabel', {
        defaultValue: 'How is company size determined?',
      })}
      options={COMPANY_SIZE_OPTIONS}
      helpEn={COMPANY_SIZE_HELP_EN}
      selected={staged.companySize}
      setSelected={set.companySize}
      facetCount={facetCount}
      facet="company_size"
    />
  );
}

/** Public procurement — two overlapping roles from EU TED notices. Unlike
 *  the legal-status buckets above, ticking both is a real filter (~1% of the
 *  register appears in TED at all), so there is no "select everything = no
 *  filter" shortcut here. The ⓘ carries the coverage caveat: TED data starts
 *  2025. */
export function TenderRoleField({
  t,
  lockOf,
  facetCount,
  staged,
  set,
}: OptionFieldProps) {
  return (
    <OptionCheckboxField
      t={t}
      lockOf={lockOf}
      control="tenderRole"
      label={t('search.sidebar.tenderRole', {
        defaultValue: 'Public procurement',
      })}
      infoLabel={t('search.sidebar.tenderRoleInfoLabel', {
        defaultValue: 'What do these roles mean?',
      })}
      options={TENDER_ROLE_OPTIONS}
      helpEn={TENDER_ROLE_HELP_EN}
      extraHelp={
        <div>
          <dd className="text-muted-foreground">
            {t('search.sidebar.tenderRoleCoverage', {
              defaultValue:
                'Source: EU TED. Covers notices published from January 2025 onwards.',
            })}
          </dd>
        </div>
      }
      selected={staged.tenderRole}
      setSelected={set.tenderRole}
      facetCount={facetCount}
      facet="tender_role"
    />
  );
}

/**
 * The profile group's boolean filters, one checkbox each under "Contact". A
 * new profile checkbox (has phone, …) is one more entry: its FilterParams
 * key, label i18n key + English default, and facet.
 */
const PROFILE_FLAGS = [
  { key: 'hasWebsite', labelKey: 'search.sidebar.hasWebsite', defaultLabel: 'Has Website', facet: 'has_website' },
  { key: 'hasEmail', labelKey: 'search.sidebar.hasEmail', defaultLabel: 'Has Email', facet: 'has_email' },
] as const satisfies readonly {
  key: keyof ProfileFilterGroupProps['staged'];
  labelKey: string;
  defaultLabel: string;
  facet: string;
}[];

/** Contact — boolean checkboxes. Headed rather than loose: they sit directly
 *  under the legal-status checkboxes and without a label of their own the
 *  two sets read as one list, so "Has Email" looked like another lifecycle
 *  state. We deliberately don't expose a "false" mode (only-without-X is
 *  rarely useful); unchecked = no filter applied. */
export function ContactField({
  t,
  lockOf,
  facetCount,
  staged,
  set,
}: OptionFieldProps) {
  // The heading is locked only when every checkbox under it is.
  const headingLock = PROFILE_FLAGS.slice(1).reduce<Lock>(
    (lock, flag) => lock && lockOf(flag.key),
    lockOf(PROFILE_FLAGS[0].key),
  );
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={headingLock} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.contact', { defaultValue: 'Contact' })}
      </FilterLabel>
      <div className="space-y-0.5">
        {PROFILE_FLAGS.map((flag) => (
          <FilterCheckbox
            key={flag.key}
            checked={staged[flag.key]}
            onCheckedChange={set[flag.key]}
            lock={lockOf(flag.key)}
            label={t(flag.labelKey, { defaultValue: flag.defaultLabel })}
            count={facetCount(flag.facet, 'true')}
          />
        ))}
      </div>
    </div>
  );
}
